import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Grid3x3 } from 'lucide-react';
import FilterBar from './components/FilterBar';
import KpiCards from './components/KpiCards';
import UsageMatrix from './components/UsageMatrix';
import CompanyBreakdown from './components/CompanyBreakdown';
import Legend from './components/Legend';
import { DEFAULT_METRIC, DEFAULT_SCALE, METRICS, SCALES, buildColumns, buildGroups, buildIntensities, companyScores, intensitiesFor, scoreCells } from './helpers';
import { useFeatureUsage } from '@/data/featureUsage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Empty, EmptyIcon, EmptyTitle, EmptyDescription } from '@/components/ui/empty';
import { Error } from '@/components/ui/error';
import { Label } from '@/components/ui/label';
import { Loading } from '@/components/ui/loading';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const DEFAULT_DAYS = 90;

const daysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
};

const today = () => new Date().toISOString().split('T')[0];

const Page = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCompanyUid, setSelectedCompanyUid] = useState(null);

  const startDate = searchParams.get('startDate') || daysAgo(DEFAULT_DAYS);
  const endDate = searchParams.get('endDate') || today();
  const companiesParam = searchParams.get('companies');
  const metric = searchParams.get('metric') || DEFAULT_METRIC;
  const scale = searchParams.get('scale') || DEFAULT_SCALE;
  const collapsed = searchParams.get('view') === 'areas';
  const sortField = searchParams.get('sort') || 'score';
  const sortDir = searchParams.get('dir') || (sortField === 'company' ? 'asc' : 'desc');

  const updateParams = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === undefined || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });
        return next;
      });
    },
    [setSearchParams]
  );

  const featureUsage = useFeatureUsage({ startDate, endDate });
  const data = featureUsage.data;

  const selectedCompanyUids = useMemo(() => (companiesParam === null ? null : new Set(companiesParam.split(',').filter(Boolean))), [companiesParam]);

  const pickerCompanies = useMemo(() => (data?.companies ?? []).map((c) => ({ uid: c.uid, name: c.company_name })).sort((a, b) => a.name.localeCompare(b.name)), [data]);

  const visibleCompanies = useMemo(() => (data?.companies ?? []).filter((c) => !selectedCompanyUids || selectedCompanyUids.has(c.uid)), [data, selectedCompanyUids]);

  const featureColumns = useMemo(() => buildColumns(data?.catalogue ?? [], false), [data]);
  const columns = useMemo(() => (collapsed ? buildColumns(data?.catalogue ?? [], true) : featureColumns), [data, collapsed, featureColumns]);
  const groups = useMemo(() => buildGroups(columns), [columns]);

  // Scored once per feature for the company score and its breakdown, and again per area
  // when collapsed, since an area ranks on its own combined counts
  const featureCells = useMemo(() => (data ? scoreCells(data.companies, data.usage, featureColumns) : {}), [data, featureColumns]);
  const columnCells = useMemo(() => (collapsed && data ? scoreCells(data.companies, data.usage, columns) : featureCells), [collapsed, data, columns, featureCells]);
  const scores = useMemo(() => companyScores(featureCells, data?.catalogue ?? []), [featureCells, data]);

  const rows = useMemo(() => {
    const built = visibleCompanies.map((company) => ({
      uid: company.uid,
      name: company.company_name,
      score: scores[company.uid],
      cells: columnCells[company.uid]
    }));
    const sign = sortDir === 'desc' ? -1 : 1;
    const byName = (a, b) => a.name.localeCompare(b.name);
    // Companies tied on a column stay in name order whichever way the column sorts
    return built.sort((a, b) => {
      if (sortField === 'score') return sign * (a.score - b.score) || byName(a, b);
      if (sortField === 'company' || !a.cells[sortField]) return sign * byName(a, b);
      return sign * ((a.cells[sortField][metric] ?? -1) - (b.cells[sortField][metric] ?? -1)) || byName(a, b);
    });
  }, [visibleCompanies, scores, columnCells, sortField, sortDir, metric]);

  const intensities = useMemo(() => buildIntensities(rows, columns, metric, scale), [rows, columns, metric, scale]);
  const scoreIntensities = useMemo(() => intensitiesFor(Object.fromEntries(rows.map((row) => [row.uid, row.score])), scale, 100), [rows, scale]);

  const activePreset = [30, 90, 365].find((days) => startDate === daysAgo(days) && endDate === today());

  const handleSort = (field) => {
    if (sortField === field) {
      updateParams({ dir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      updateParams({ sort: field === 'score' ? null : field, dir: null });
    }
  };

  const handleCompaniesChange = (uids) => {
    updateParams({ companies: uids === null ? null : [...uids].join(',') || ',' });
  };

  if (featureUsage.isLoading && !data) {
    return <Loading />;
  }

  if (featureUsage.error) {
    return <Error message="An error occurred loading feature usage" />;
  }

  const selectedCompany = data.companies.find((c) => c.uid === selectedCompanyUid);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">Feature Usage</h1>
          <p className="text-sm text-muted-foreground">Which companies use which features, and how much compared to each other</p>
        </div>
        <FilterBar
          startDate={startDate}
          endDate={endDate}
          activePreset={activePreset}
          companies={pickerCompanies}
          selectedCompanyUids={selectedCompanyUids}
          onStartDateChange={(v) => updateParams({ startDate: v })}
          onEndDateChange={(v) => updateParams({ endDate: v })}
          onPreset={(days) => updateParams({ startDate: days === DEFAULT_DAYS ? null : daysAgo(days), endDate: null })}
          onCompaniesChange={handleCompaniesChange}
          onReset={() => setSearchParams({})}
        />
      </div>

      <KpiCards companies={visibleCompanies} catalogue={data.catalogue} usage={data.usage} />

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-4">
          <CardTitle className="text-base">Usage by company and feature</CardTitle>
          <div className="flex flex-wrap items-center gap-4">
            <Tabs value={scale} onValueChange={(v) => updateParams({ scale: v === DEFAULT_SCALE ? null : v })}>
              <TabsList className="h-8">
                {SCALES.map((s) => (
                  <TabsTrigger key={s.key} value={s.key} className="text-xs">
                    {s.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <Switch id="feature-usage-areas" checked={collapsed} onCheckedChange={(checked) => updateParams({ view: checked ? 'areas' : null, sort: null, dir: null })} />
              <Label htmlFor="feature-usage-areas" className="text-xs">
                Group by area
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <Tabs value={metric} onValueChange={(v) => updateParams({ metric: v === DEFAULT_METRIC ? null : v })}>
              <TabsList className="h-8">
                {METRICS.map((m) => (
                  <TabsTrigger key={m.key} value={m.key} className="text-xs">
                    {m.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Legend scale={scale} />
          </div>

          {rows.length === 0 ? (
            <Empty>
              <EmptyIcon>
                <Grid3x3 />
              </EmptyIcon>
              <EmptyTitle>No Data</EmptyTitle>
              <EmptyDescription>{data.companies.length === 0 ? 'No active companies found' : 'No companies selected'}</EmptyDescription>
            </Empty>
          ) : (
            <UsageMatrix
              rows={rows}
              columns={columns}
              groups={groups}
              collapsed={collapsed}
              metric={metric}
              intensities={intensities}
              scoreIntensities={scoreIntensities}
              sortField={sortField}
              sortDir={sortDir}
              onSort={handleSort}
              onSelectCompany={setSelectedCompanyUid}
            />
          )}
          <p className="text-xs text-muted-foreground">Click a company for its full breakdown.</p>
        </CardContent>
      </Card>

      <CompanyBreakdown
        company={selectedCompany}
        catalogue={data.catalogue}
        cells={featureCells[selectedCompanyUid]}
        score={scores[selectedCompanyUid]}
        onClose={() => setSelectedCompanyUid(null)}
      />
    </div>
  );
};

export default Page;
